import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useViewport } from '@tryhackme/thm-ui-components';
import { useAppDispatch } from 'src/app/hooks';
import { setTaskStates } from 'src/app/reducers/room/room.reducer';
import { ACCORDION_ANIMATION_DURATION } from 'src/features/room/components/tasks/components/accordion/accordion.constants';
import { setMessage } from 'src/app/reducers/split-screen.reducer';
import { useStartVm } from 'src/common/hooks/use-start-vm';
import { isSupportedTask } from '../../hooks/use-combine-start-machine-attackbox-widget-experiment';
import { Accordion } from './components/accordion';
import { ImageModal } from './components/image-modal';
import { StyledLoadingRectangle, StyledLoadingWrapper, StyledTaskWrapper } from './tasks.styles';
import { TasksProps } from './tasks.types';
import { useShouldSeeStartVmExpand } from './tasks.helpers';
import { useLockedTasks } from './hooks';
export const Tasks: FC<TasksProps> = ({
  activeMachine,
  attackBoxPrivateIP,
  tasks,
  isLoading,
  status,
  roomId,
  joinRoom,
  roomCode,
  roomType,
  hasUserJoinedRoom,
  isNetworkRoom,
  isPublicRoom,
  networkTooltipShown,
  glossaryData,
  handleTaskContentClick,
  setActiveGlossary,
  cloud,
  isAttackboxWidgetV1ExperimentEnabled,
  isAttackboxWidgetV2ExperimentEnabled,
  hasMultipleVMs,
  autoAnswerQuestions
}) => {
  const dispatch = useAppDispatch();
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null);
  const accordions = useRef<Map<number, HTMLDivElement>>(new Map());
  const [imageElement, setImageElement] = useState<HTMLImageElement | null>(null);
  const {
    width
  } = useViewport();
  const {
    startVmHandler
  } = useStartVm(roomCode);
  const shouldSeeVmStart = useShouldSeeStartVmExpand(roomCode, activeAccordion);
  const [isDesktopOnlyAccessPopupOpen, setIsDesktopOnlyAccessPopupOpen] = useState(false);
  const lockedTasks = useLockedTasks(isPublicRoom, tasks);
  const lockedTaskNumbers = useMemo(() => lockedTasks.map(task => task.taskNo), [lockedTasks]);
  useEffect(() => {
    // if a task becomes locked (due to reset), close the accordion if it was open
    if (activeAccordion && lockedTaskNumbers.includes(activeAccordion)) {
      setActiveAccordion(null);
    }
  }, [activeAccordion, lockedTaskNumbers]);
  const sortedTasks = useMemo(() => {
    if (tasks && status === 'success') {
      const data = [...tasks];
      data?.sort((task1, task2) => task1.taskNo - task2.taskNo);
      return data;
    }
    return [];
  }, [tasks, status]);
  useEffect(() => {
    // Initial incomplete task expand logic
    if (sortedTasks && sortedTasks.length > 0 && !activeAccordion) {
      const initialOpenTask = sortedTasks.find(task => task.questions?.some(question => !question.progress?.correct));
      if (initialOpenTask) {
        setActiveAccordion(initialOpenTask.taskNo);
        // Wait a short period of time for tasks to render before determining auto scroll position and executing, but disabled when:
        //  In tutorial room since we want to stay on the top of the page to show helper tooltip
        //  In a network room if the user has already joined the room and the network tooltip is not shown
        setTimeout(() => {
          if (roomCode !== 'tutorial' && !(hasUserJoinedRoom && isNetworkRoom && !networkTooltipShown) && initialOpenTask.taskNo !== 1) {
            scrollToAccordion(initialOpenTask.taskNo ?? 1);
          }
        }, ACCORDION_ANIMATION_DURATION + 50);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedTasks]);
  const [hasSeenVmStart, setHasSeenVmStart] = useState(false);
  useEffect(() => {
    if (activeAccordion !== 2 || roomCode !== 'offensivesecurityintro') {
      return;
    }
    if (activeMachine) return;
    if (hasSeenVmStart) return;
    const currentTask = sortedTasks[activeAccordion - 1];
    if (!currentTask) return;
    const {
      _id: taskId,
      uploadId
    } = currentTask;
    if (shouldSeeVmStart) {
      dispatch(setMessage('Use this machine to complete the hands-on exercise of Task 2'));
      const startVmAsync = async () => {
        await startVmHandler('Deploy', uploadId, taskId);
        setHasSeenVmStart(true);
      };
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      startVmAsync();
    } else {
      dispatch(setMessage('Use the AttackBox to attack machines you start on tasks'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSeeVmStart, hasSeenVmStart, dispatch, activeAccordion, sortedTasks, startVmHandler, width, activeMachine, roomCode]);
  useEffect(() => {
    const newTaskQuestionStates = sortedTasks.map(sortedTask => sortedTask.questions.map(question => question.progress?.correct ?? false));
    dispatch(setTaskStates([...newTaskQuestionStates]));
  }, [dispatch, sortedTasks]);
  const handleAccordionChange = (taskNumber: number) => {
    setActiveAccordion(prev => prev === taskNumber ? null : taskNumber);
    setActiveGlossary(null);
    setTimeout(() => scrollToAccordion(taskNumber), ACCORDION_ANIMATION_DURATION);
  };
  const scrollToAccordion = (taskNumber: number) => {
    const activeElement = accordions.current.get(taskNumber);
    activeElement?.scrollIntoView?.({
      behavior: 'smooth',
      block: 'start',
      inline: 'start'
    });
  };
  if (isLoading) {
    return <StyledLoadingWrapper data-testid="loading-container">
        <StyledLoadingRectangle />
        <StyledLoadingRectangle />
        <StyledLoadingRectangle />
      </StyledLoadingWrapper>;
  }
  return <StyledTaskWrapper onClick={e => setImageElement((e.target as HTMLImageElement))} data-sentry-element="StyledTaskWrapper" data-sentry-component="Tasks" data-sentry-source-file="tasks.tsx">
      <ImageModal imageElement={imageElement} setImageElement={setImageElement} data-sentry-element="ImageModal" data-sentry-source-file="tasks.tsx" />
      {sortedTasks?.map(task => <Accordion ref={element => {
      if (element) {
        accordions.current.set(task.taskNo, element);
      }
    }} activeMachine={activeMachine} attackBoxPrivateIP={attackBoxPrivateIP} {...task} key={task.taskNo} activeAccordion={activeAccordion} handleAccordionChange={handleAccordionChange} roomId={roomId} joinRoom={joinRoom} hasUserJoinedRoom={hasUserJoinedRoom} roomCode={roomCode} roomType={roomType} glossaryData={glossaryData} handleTaskContentClick={handleTaskContentClick} isDesktopOnlyAccessPopupOpen={isDesktopOnlyAccessPopupOpen} setIsDesktopOnlyAccessPopupOpen={setIsDesktopOnlyAccessPopupOpen} hasSeenVmStart={hasSeenVmStart} cloud={cloud} hasMultipleVMs={hasMultipleVMs} isAttackboxWidgetV1ExperimentEnabled={isAttackboxWidgetV1ExperimentEnabled && isSupportedTask(task.type)} isAttackboxWidgetV2ExperimentEnabled={isAttackboxWidgetV2ExperimentEnabled && isSupportedTask(task.type)} autoAnswerQuestions={autoAnswerQuestions} isLocked={lockedTasks.includes(task)} />)}
    </StyledTaskWrapper>;
};